import { DriftBackground } from './drift';
import type { BackgroundContext, BackgroundScene } from './types';

export type { BackgroundContext, BackgroundScene } from './types';

/** `drift` is the shipped default; the rest are DEV-only candidates. */
export const BACKGROUND_IDS = ['drift', 'automaton', 'automaton-full', 'signal', 'flow'] as const;
export type BackgroundId = (typeof BACKGROUND_IDS)[number];

export const DEFAULT_BACKGROUND: BackgroundId = 'drift';

export function isBackgroundId(value: string): value is BackgroundId {
  return (BACKGROUND_IDS as readonly string[]).includes(value);
}

/**
 * The default is imported statically — it is on the critical path, and a
 * dynamic import would cost a second round trip before anything renders. Every
 * other candidate sits behind `import.meta.env.DEV` *and* a dynamic import, so
 * the branch folds to a constant `false` in a production build and Rollup drops
 * their chunks entirely. Prod ships `drift` and nothing else.
 */
export async function createBackground(
  id: BackgroundId,
  ctx: BackgroundContext,
): Promise<BackgroundScene> {
  if (import.meta.env.DEV) {
    if (id === 'automaton' || id === 'automaton-full') {
      const { AutomatonBackground } = await import('./automaton');
      return new AutomatonBackground(ctx, id === 'automaton-full');
    }
    if (id === 'signal') {
      const { SignalBackground } = await import('./signal');
      return new SignalBackground(ctx);
    }
    if (id === 'flow') {
      const { FlowBackground } = await import('./flow');
      return new FlowBackground(ctx);
    }
  }
  return new DriftBackground(ctx);
}
